import React, { useRef, useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Upload, Loader2, X, FileText, ImageIcon, Paperclip } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_BYTES = 10 * 1024 * 1024; // 10MB per file
const ACCEPT = 'image/*,application/pdf';

/**
 * FileMultiUpload — multi-file upload (images + PDFs) to Cloudinary via signed
 * request. Stores results as a list of attachment objects.
 *
 * Props:
 *   value: array of attachment objects [{url, public_id, filename, size, resource_type, content_type}]
 *   onChange: (next: array) => void
 *   folder: cloudinary folder
 *   testId: prefix for data-testid attributes
 */
export const FileMultiUpload = ({ value = [], onChange, folder, testId }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handlePick = () => inputRef.current?.click();

  const isImage = (item) =>
    (item.content_type || '').startsWith('image/') ||
    item.resource_type === 'image' ||
    /\.(png|jpe?g|webp|gif|heic|heif)$/i.test(item.filename || item.url || '');

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (files.length === 0) return;

    setUploading(true);
    try {
      const sigRes = await axios.get(`${BACKEND_URL}/api/cloudinary/signature`, {
        params: { folder, resource_type: 'auto' },
        withCredentials: true,
      });
      const { signature, timestamp, cloud_name, api_key, folder: sigFolder } = sigRes.data;
      const uploaded = [];
      for (const file of files) {
        if (file.size > MAX_BYTES) {
          toast.error(`${file.name}: máximo 10MB.`);
          continue;
        }
        const form = new FormData();
        form.append('file', file);
        form.append('api_key', api_key);
        form.append('timestamp', timestamp);
        form.append('signature', signature);
        form.append('folder', sigFolder);
        const up = await fetch(
          `https://api.cloudinary.com/v1_1/${cloud_name}/auto/upload`,
          { method: 'POST', body: form }
        );
        const j = await up.json();
        if (!up.ok) {
          toast.error(`${file.name}: ${j?.error?.message || up.status}`);
          continue;
        }
        uploaded.push({
          url: j.secure_url,
          public_id: j.public_id,
          filename: file.name,
          size: file.size,
          resource_type: j.resource_type,
          content_type: file.type,
        });
      }
      if (uploaded.length > 0) {
        onChange([...(value || []), ...uploaded]);
        toast.success(`${uploaded.length} arquivo(s) enviado(s).`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Falha no upload');
    } finally {
      setUploading(false);
    }
  };

  const remove = (idx) => {
    const next = [...value];
    next.splice(idx, 1);
    onChange(next);
  };

  return (
    <div className="space-y-2">
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        multiple
        onChange={handleFiles}
        className="hidden"
        data-testid={testId ? `${testId}-input` : undefined}
      />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {(value || []).map((att, idx) => (
          <div
            key={att.public_id || idx}
            className="relative group border border-border rounded-lg p-2 bg-muted/30"
            data-testid={testId ? `${testId}-item-${idx}` : undefined}
          >
            <a
              href={att.url}
              target="_blank"
              rel="noreferrer"
              className="block aspect-square rounded-md overflow-hidden bg-slate-100 flex items-center justify-center"
            >
              {isImage(att) ? (
                <img
                  src={att.url}
                  alt={att.filename || 'anexo'}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="flex flex-col items-center text-slate-500">
                  <FileText className="h-8 w-8" />
                  <span className="text-[10px] mt-1 font-bold">PDF</span>
                </div>
              )}
            </a>
            <div className="mt-1 text-[11px] truncate" title={att.filename}>
              {att.filename || att.url.split('/').pop()}
            </div>
            <button
              type="button"
              onClick={() => remove(idx)}
              className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-destructive text-white flex items-center justify-center shadow-sm opacity-0 group-hover:opacity-100 transition-opacity"
              title="Remover"
              data-testid={testId ? `${testId}-remove-${idx}` : undefined}
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={handlePick}
          disabled={uploading}
          className="aspect-square rounded-lg border-2 border-dashed border-slate-200 hover:border-slate-300 bg-slate-50 hover:bg-slate-100 transition-colors flex flex-col items-center justify-center gap-1 text-slate-500"
          data-testid={testId ? `${testId}-add-btn` : undefined}
        >
          {uploading ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" />
              <span className="text-xs">Enviando...</span>
            </>
          ) : (
            <>
              <Paperclip className="h-5 w-5" />
              <span className="text-[11px] font-medium">Adicionar</span>
              <span className="text-[10px]">PDF · imagens</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
};

// Re-export icon for callers that want to render a default thumbnail elsewhere.
export const FileMultiUploadIcon = ImageIcon;
export const FileMultiUploadFolderIcon = Upload;
